import {
  captureActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  type ActiveSessionAuthGenerationSnapshot,
} from './activeSessionSync';
import { hasSsoSession, type SsoContext } from './ssoSession';

type MutationGenerationStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface P1SsoLockManager {
  request<T>(
    name: string,
    options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T>;
}

export type P1SsoAuthMutationResult<T> =
  | { kind: 'completed'; value: T }
  | {
    kind: 'skipped';
    reason:
      | 'locks_unavailable'
      | 'lock_failed'
      | 'stale_intent'
      | 'storage_unavailable';
  };

const MUTATION_GENERATION_PREFIX = 'ra2n_sso_auth_mutation_generation';
const MUTATION_CLAIMED_AT_PREFIX = 'ra2n_sso_auth_mutation_claimed_at';

/**
 * How long an ODD claim is believed to be live when no cross-document lock can vouch for it.
 *
 * With real Web Locks (secure contexts) the question never arises: the claim is written inside
 * the exclusive lock and the terminal even value inside its `finally`, so a caller that HOLDS the
 * lock and still reads an odd value knows the previous holder's document died mid-flight (a lock
 * is released with its document). The in-tab fallback on plain http has no such guarantee — a
 * different tab may genuinely be mid-request — so there the stamp below decides: a real mutation
 * is one request and completes in well under a second; a minute is generous enough never to
 * mistake a live one for debris, and short enough that a stranded one costs a pause, not a dead
 * Portal. Measured on dev1 06/09/2026: a P1 reload during its own active-session-sync (F5 twice
 * about 1.5 s apart, or a 開示 reload at the same moment) left the sentinel odd for good — logout
 * did nothing, every 開示 → P1 completion failed, only a manual P1 login repaired it.
 */
const ABANDONED_CLAIM_TTL_MS = 60_000;

/**
 * Hàng đợi thay thế khi trình duyệt không có Web Locks.
 *
 * Web Locks CHỈ tồn tại trong secure context, nên một deployment nội bộ chạy http (IP:cổng, không
 * chứng chỉ) luôn có `navigator.locks === undefined`. Khi đó runP1SsoAuthMutation trả về
 * { kind: 'skipped', reason: 'locks_unavailable' } và hậu quả không phải "kém an toàn hơn" mà là
 * TẮT HẲN: passive SSO không khởi động, đăng nhập báo "không hoàn tất an toàn".
 *
 * Lock ở đây để tuần tự hoá thao tác cookie-auth GIỮA CÁC TAB. Trình duyệt không có API đó thì
 * cũng không tab nào giành được lock để mà tranh, nên xếp hàng trong chính tab này là mức mạnh
 * nhất còn khả dụng, và vẫn giữ đúng thứ tự cho mọi thao tác cùng tên.
 */
const inTabLockQueues = new Map<string, Promise<unknown>>();

const inTabLockManager = (): P1SsoLockManager => ({
  request: <T,>(
    name: string,
    _options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T> => {
    const previous = inTabLockQueues.get(name) ?? Promise.resolve();
    const run = previous.then(
      () => callback(),
      () => callback(),
    );

    inTabLockQueues.set(
      name,
      run.then(
        () => undefined,
        () => undefined,
      ),
    );

    return run;
  },
});

const browserLockManager = (): P1SsoLockManager | undefined => {
  if (typeof navigator === 'undefined' || navigator.locks === undefined) {
    return typeof window === 'undefined' ? undefined : inTabLockManager();
  }

  return {
    request: async (name, options, callback) =>
      await navigator.locks.request(name, options, () => callback()),
  };
};

const lockName = (context: SsoContext): string =>
  `ra2n:sso-auth-mutation:${context.site}:${context.entryType}`;

const mutationGenerationKey = (context: SsoContext): string =>
  `${MUTATION_GENERATION_PREFIX}:${context.site}:${context.entryType}`;

const mutationClaimedAtKey = (context: SsoContext): string =>
  `${MUTATION_CLAIMED_AT_PREFIX}:${context.site}:${context.entryType}`;

/** Real Web Locks serialize across documents; the in-tab queue (plain http) does not. */
const hasCrossDocumentLocks = (): boolean =>
  typeof navigator !== 'undefined' && navigator.locks !== undefined;

/**
 * Odd claims this document published and has not yet replaced, keyed by sentinel key.
 *
 * WHY THE `finally` IS NOT ENOUGH. The terminal even value is written in a `finally`, and a
 * `finally` does not run when the document is torn down mid-mutation — which happens on purpose
 * (a login leaves for the broker's establish hop, a sync leaves for its authorize round trip) and
 * by accident (a second F5, a typed address, a closed tab). `pagehide` is the last event before
 * the document is discarded or frozen, so it is where the claim gets the release its `finally`
 * could not give it. Only this document's OWN claim is released: another document may have
 * claimed since, and stamping over its live claim would strand that one instead.
 */
const publishedClaims = new Map<string, { storage: MutationGenerationStorage; value: number }>();

export const releasePublishedP1MutationClaims = (): void => {
  for (const [key, { storage, value }] of publishedClaims) {
    try {
      if (storage.getItem(key) === String(value)) {
        storage.setItem(key, String(value + 1));
      }
    } catch {
      // The page is going away; nothing to report to, and the TTL / lock still cover the claim.
    }
  }
  publishedClaims.clear();
};

const registerClaimRelease = (): void => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return;
  }
  // Registered on every claim rather than once behind a flag: addEventListener ignores a repeat
  // of the same (type, listener) pair, so this is idempotent, and a once-only flag would bind to
  // whichever `window` existed at the first claim. NOT `visibilitychange`: a hidden tab is a tab
  // switch, and its mutation is still legitimately in flight.
  window.addEventListener('pagehide', releasePublishedP1MutationClaims);
};

const claimIsAbandoned = (
  context: SsoContext,
  storage: MutationGenerationStorage,
  now: number,
): boolean => {
  const stamped = storage.getItem(mutationClaimedAtKey(context));
  if (stamped === null || !/^\d+$/.test(stamped)) {
    // A claim from before this format carries no stamp and cannot be aged on evidence; it keeps
    // the original fail-closed rule (only the caller's anonymous-context revalidation repairs it).
    return false;
  }
  const claimedAt = Number(stamped);
  return !Number.isFinite(claimedAt) || now - claimedAt > ABANDONED_CLAIM_TTL_MS || claimedAt > now;
};

/**
 * A terminated page can leave the durable mutation sentinel odd. Only a fresh
 * direct admin login with no local or SSO session may repair that abandoned
 * sentinel. The caller evaluates this again while holding the exact context's
 * Web Lock so a successor session can never be overwritten by the recovery.
 */
export const canRecoverAnonymousP1AdminMutation = ({
  context,
  authGeneration,
  localStorage,
  sessionStorage,
}: {
  context: SsoContext;
  authGeneration: ActiveSessionAuthGenerationSnapshot;
  localStorage: Pick<Storage, 'getItem'>;
  sessionStorage: Pick<Storage, 'getItem'>;
}): boolean =>
  context.entryType === 'admin' &&
  localStorage.getItem('site_admin_token') === null &&
  localStorage.getItem('site_admin') === null &&
  localStorage.getItem('site_admin_site') === null &&
  !hasSsoSession(context, sessionStorage) &&
  isActiveSessionAuthGenerationCurrent(authGeneration, localStorage);

/**
 * Symmetric to canRecoverAnonymousP1AdminMutation, for the clinic/unified entry types that share
 * the auth_token/auth_user/auth_clinic local session keys instead of the admin ones.
 *
 * Without this, a mutation interrupted mid-flight (tab closed, network dropped) while the
 * generation sentinel was odd permanently blocked every future clinic login from that browser —
 * AuthContext's login() never passed a `canRecoverAbandonedMutation`, so `runP1SsoAuthMutation`
 * always fell into the fail-closed default once poisoned. The admin entry already had this safety
 * valve; clinic/unified never got the symmetric fix.
 */
export const canRecoverAnonymousP1ClinicMutation = ({
  context,
  authGeneration,
  localStorage,
  sessionStorage,
}: {
  context: SsoContext;
  authGeneration: ActiveSessionAuthGenerationSnapshot;
  localStorage: Pick<Storage, 'getItem'>;
  sessionStorage: Pick<Storage, 'getItem'>;
}): boolean =>
  (context.entryType === 'clinic' || context.entryType === 'unified') &&
  localStorage.getItem('auth_token') === null &&
  localStorage.getItem('auth_user') === null &&
  localStorage.getItem('auth_clinic') === null &&
  !hasSsoSession(context, sessionStorage) &&
  isActiveSessionAuthGenerationCurrent(authGeneration, localStorage);

const readMutationGeneration = (
  context: SsoContext,
  storage: MutationGenerationStorage,
): number => {
  const stored = storage.getItem(mutationGenerationKey(context));
  if (stored === null) {
    return 0;
  }
  if (!/^\d+$/.test(stored)) {
    throw new Error('P1 SSO mutation generation storage is invalid.');
  }
  const parsed = Number(stored);
  if (
    !Number.isSafeInteger(parsed) ||
    parsed < 0 ||
    parsed > Number.MAX_SAFE_INTEGER - 2
  ) {
    throw new Error('P1 SSO mutation generation storage is invalid.');
  }
  return parsed;
};

const writeMutationGeneration = (
  context: SsoContext,
  storage: MutationGenerationStorage,
  generation: number,
): void => {
  storage.setItem(mutationGenerationKey(context), String(generation));
};

/**
 * Serializes every cookie-auth mutation for one P1 Portal entry across tabs.
 *
 * The callback boundary intentionally includes the network response, browser
 * Set-Cookie processing, local storage/state updates and generation event. A
 * queued intent is discarded after acquiring the lock when another tab has
 * already committed a newer auth generation.
 */
export const runP1SsoAuthMutation = async <T>({
  context,
  ssoEnabled,
  storage,
  operation,
  canRunAfterCommittedMutation,
  canRecoverAbandonedMutation,
  locks: providedLocks,
  locksAreCrossDocument: providedLocksAreCrossDocument,
  now = Date.now,
}: {
  context: SsoContext;
  ssoEnabled: boolean;
  storage: MutationGenerationStorage;
  operation: () => Promise<T>;
  /**
   * A queued exact-session operation may outlive an earlier mutation that did
   * not change the auth generation (for example an active-sync probe). The
   * caller may revalidate its exact credential snapshot under the same lock
   * and supersede only that fully committed mutation. A missing/failed check
   * keeps the default fail-closed stale-intent behavior.
   */
  canRunAfterCommittedMutation?: () => boolean;
  /**
   * Revalidate an exact anonymous context under the acquired Web Lock before
   * repairing a durable odd sentinel left by a terminated prior page.
   */
  canRecoverAbandonedMutation?: () => boolean;
  locks?: P1SsoLockManager | null;
  /**
   * Whether `locks` serializes across documents (real Web Locks). Under such a lock an odd
   * sentinel read by the holder can only be an abandoned claim, so it is repaired outright.
   */
  locksAreCrossDocument?: boolean;
  now?: () => number;
}): Promise<P1SsoAuthMutationResult<T>> => {
  if (!ssoEnabled) {
    return { kind: 'completed', value: await operation() };
  }

  // A caller-supplied lock manager says nothing about its reach; only the browser's own
  // navigator.locks is known to serialize across documents.
  const locks = providedLocks === undefined ? browserLockManager() : providedLocks;
  const locksAreCrossDocument = providedLocksAreCrossDocument
    ?? (providedLocks === undefined && hasCrossDocumentLocks());

  if (locks === undefined || locks === null) {
    return { kind: 'skipped', reason: 'locks_unavailable' };
  }

  let intentGeneration: ReturnType<typeof captureActiveSessionAuthGeneration>;
  let intentMutationGeneration: number;
  try {
    intentGeneration = captureActiveSessionAuthGeneration(context, storage);
    intentMutationGeneration = readMutationGeneration(context, storage);
  } catch {
    return { kind: 'skipped', reason: 'storage_unavailable' };
  }
  let callbackStarted = false;

  try {
    return await locks.request(lockName(context), { mode: 'exclusive' }, async () => {
      callbackStarted = true;
      let currentMutationGeneration: number;
      let authGenerationCurrent: boolean;
      try {
        currentMutationGeneration = readMutationGeneration(context, storage);
        authGenerationCurrent = isActiveSessionAuthGenerationCurrent(
          intentGeneration,
          storage,
        );
      } catch {
        return { kind: 'skipped', reason: 'storage_unavailable' } as const;
      }

      if (!authGenerationCurrent) {
        return { kind: 'skipped', reason: 'stale_intent' } as const;
      }

      // Odd generations are in-progress markers. If one remains after the lock becomes
      // available, the previous mutation could not commit its terminal sentinel. It is repaired
      // when (a) the lock is a real cross-document lock — then the previous holder's document is
      // gone, the claim cannot be live — or (b) the claim's stamp is older than the TTL (in-tab
      // fallback), or (c) the caller revalidates an exact anonymous context under this lock.
      if (currentMutationGeneration % 2 !== 0) {
        let canRecover = false;
        try {
          canRecover = locksAreCrossDocument
            || claimIsAbandoned(context, storage, now())
            || canRecoverAbandonedMutation?.() === true;
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        if (!canRecover) {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }

        currentMutationGeneration += 1;
        try {
          writeMutationGeneration(context, storage, currentMutationGeneration);
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        intentMutationGeneration = currentMutationGeneration;
      }

      if (currentMutationGeneration !== intentMutationGeneration) {
        let stillCurrent = false;
        try {
          stillCurrent = canRunAfterCommittedMutation?.() === true;
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        if (!stillCurrent) {
          return { kind: 'skipped', reason: 'stale_intent' } as const;
        }
      }

      const inProgressGeneration = currentMutationGeneration + 1;
      const committedGeneration = currentMutationGeneration + 2;
      try {
        // Claim before the request. If the terminal write fails, the odd value
        // remains and causes every later mutation to fail closed.
        writeMutationGeneration(context, storage, inProgressGeneration);
        try {
          storage.setItem(mutationClaimedAtKey(context), String(now()));
        } catch {
          // Without a stamp the claim simply keeps the original fail-closed rule.
        }
        publishedClaims.set(mutationGenerationKey(context), { storage, value: inProgressGeneration });
        registerClaimRelease();
      } catch {
        return { kind: 'skipped', reason: 'storage_unavailable' } as const;
      }

      try {
        return { kind: 'completed', value: await operation() } as const;
      } finally {
        // Set-Cookie is applied before the request promise settles. Advance the
        // independent sentinel even when metadata/state/event handling throws.
        // If THIS throws the entry stays tracked on purpose: `pagehide` is the only remaining
        // chance to release the claim before a cross-document lock or the TTL does.
        writeMutationGeneration(context, storage, committedGeneration);
        publishedClaims.delete(mutationGenerationKey(context));
      }
    });
  } catch (error) {
    if (callbackStarted) {
      throw error;
    }
    return { kind: 'skipped', reason: 'lock_failed' };
  }
};
